import React, { useState } from "react";

import { useTranslate } from "localization";
import { Modal } from "minimum-ui/core";
import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";
import { H4M } from "typography/headers";

import { noop } from "utilities/general";

import { ShowMoreNumber } from "Common/styles";

export interface ListProps {
  items: string | string[];
  noun: string;
  max?: number;
  disableLink?: boolean;
  alphabeticalSort?: boolean;
  onlyNumber?: boolean;
  customColor?: string;
}

/**
 * Renders a list of text items up to a max length,
 * if length is exceeded the list is summarized and an tooltip
 */
const List: React.FC<ListProps> = ({
  items,
  noun,
  max = 1,
  disableLink,
  alphabeticalSort,
  onlyNumber,
  customColor,
}) => {
  const { translate } = useTranslate();

  const [showModal, setShowModal] = useState(false);
  const array = Array.isArray(items) ? items : [items];
  const sortedArray = alphabeticalSort
    ? array.sort((a, b) => a.localeCompare(b))
    : array;
  const copy = sortedArray.join(", ");

  if ((sortedArray.length <= max || max === 0) && !onlyNumber) {
    return <>{copy}</>;
  }

  return (
    <>
      {onlyNumber ? (
        <ShowMoreNumber
          onClick={() => {
            disableLink ? noop : setShowModal(true);
          }}
          $textColor={customColor}
        >
          {sortedArray.length}
        </ShowMoreNumber>
      ) : (
        <ShowMore
          $disabled={disableLink}
          onClick={() => {
            disableLink ? noop : setShowModal(true);
          }}
        >
          {sortedArray.slice(0, max).join(", ")}, +{sortedArray.length - max}{" "}
          {translate("more")}
        </ShowMore>
      )}

      {showModal && (
        <Modal
          isOpen={showModal}
          onDismiss={() => {
            setShowModal(false);
          }}
          title={`${sortedArray.length} ${noun}`}
          content={
            <Wrapper>
              {sortedArray.map((item) => (
                <Entry key={item}>
                  <H4M>{item}</H4M>
                </Entry>
              ))}
            </Wrapper>
          }
        />
      )}
    </>
  );
};

const ShowMore = styled.span<{ $disabled?: boolean }>`
  ${(props) =>
    !props.$disabled &&
    css`
      &:hover {
        color: ${color.blue[300]};
        text-decoration: underline;
      }

      cursor: pointer;
    `}
`;

const Wrapper = styled.ul`
  border: 1px solid ${color.neutral[200]};
  border-radius: 4px;
  list-style: none;
  padding: 8px 0;
  margin: 0;
`;

const Entry = styled.li`
  padding: 8px 16px;

  &:not(:last-child) {
    border-bottom: 1px solid ${color.neutral[200]};
  }
`;

export default List;
